import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Lock, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error(t("reset_password_too_short")); return; }
    if (pw !== pw2) { toast.error(t("reset_password_mismatch")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(t("reset_password_success"));
      setTimeout(() => navigate("/", { replace: true }), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reset_password_invalid_token"));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center" data-testid="reset-no-token">
        <AlertCircle className="w-16 h-16 text-[#C85A32] mx-auto" />
        <h1 className="mt-6 text-3xl font-heading font-black text-[#1A1A18]">
          {t("reset_password_invalid_token")}
        </h1>
        <Link to="/forgot-password" className="mt-6 btn-primary inline-flex">
          {t("forgot_password_title")}
        </Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 sm:px-6 py-16 text-center" data-testid="reset-done">
        <CheckCircle2 className="w-16 h-16 text-[#2A5C38] mx-auto" />
        <h1 className="mt-6 text-3xl font-heading font-black text-[#1A1A18]">
          {t("reset_password_success")}
        </h1>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="reset-password-page">
      <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight text-[#1A1A18]">
        {t("reset_password_title")}
      </h1>
      <p className="mt-3 text-base text-[#4A4A45]">{t("reset_password_sub")}</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-3" data-testid="reset-password-form">
        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-4 h-4 text-[#7A7A73]" />
          <input
            data-testid="reset-password-new"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder={t("reset_password_new")}
            required
            minLength={6}
            autoComplete="new-password"
            className="w-full pl-10 bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
          />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-3.5 w-4 h-4 text-[#7A7A73]" />
          <input
            data-testid="reset-password-confirm"
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder={t("reset_password_confirm")}
            required
            minLength={6}
            autoComplete="new-password"
            className="w-full pl-10 bg-white border border-[#D1CFC7] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C85A32]"
          />
        </div>
        <button
          type="submit"
          data-testid="reset-password-submit"
          disabled={busy}
          className="btn-primary w-full disabled:opacity-60"
        >
          {busy ? "..." : t("reset_password_submit")}
        </button>
      </form>
    </div>
  );
}
